import {
  CheckCircleOutlined,
  DownOutlined,
  PlusOutlined
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography
} from "antd";
import React, { SetStateAction } from "react";
import { BsBoxSeam } from "react-icons/bs";
import { FaRegClock } from "react-icons/fa6";

import { features } from "@/config/features";
import { IBulkStageSelection } from "@/pages/ForecastingPage";
import {
  IForecastingCentralRepoRes,
  IForecastingRes
} from "@/types/forecasting";

const { Text } = Typography;

export const AddStagesStep = ({
  projectCentralRepoData,
  monthForecastData,
  selectedPlots,
  bulkStageSelections,
  setBulkStageSelections
}: {
  projectCentralRepoData: IForecastingCentralRepoRes;
  monthForecastData?: IForecastingRes;
  selectedPlots: string[];
  bulkStageSelections: IBulkStageSelection[];
  setBulkStageSelections: React.Dispatch<SetStateAction<IBulkStageSelection[]>>;
}) => {
  const [expandedPlots, setExpandedPlots] = React.useState<string[]>(
    selectedPlots.length > 0 ? [selectedPlots[0]] : []
  );

  const availableStages = React.useMemo(() => {
    const stages = new Set<string>();

    projectCentralRepoData.groundworks.plots.forEach((plot) => {
      plot?.stages?.forEach((stage) => {
        const stageName = stage?.stageName;

        if (stageName && !stages.has(stageName)) {
          stages.add(stageName);
        }
      });
    });

    return [...stages];
  }, [projectCentralRepoData]);

  const togglePlotExpanded = (plotNumber: string) => {
    setExpandedPlots((prev) =>
      prev.includes(plotNumber)
        ? prev.filter((p) => p !== plotNumber)
        : [...prev, plotNumber]
    );
  };

  const handleAddStageToPlots = (stageId: string, plotNumbers: string[]) => {
    setBulkStageSelections((prev) => {
      const existing = prev.find((s) => s.stageId === stageId);
      if (existing) {
        return prev.map((s) =>
          s.stageId === stageId
            ? {
                ...s,
                plotNumbers: [...new Set([...s.plotNumbers, ...plotNumbers])]
              }
            : s
        );
      }
      return [...prev, { stageId, plotNumbers }];
    });
  };

  const handleRemoveStageFromPlot = (stageId: string, plotNumber: string) => {
    setBulkStageSelections((prev) =>
      prev
        .map((s) =>
          s.stageId === stageId
            ? {
                ...s,
                plotNumbers: s.plotNumbers.filter((p) => p !== plotNumber)
              }
            : s
        )
        .filter((s) => s.plotNumbers.length > 0)
    );
  };

  const handleToggleStage = (
    plotNumber: string,
    stageId: string,
    checked: boolean
  ) => {
    if (checked) {
      handleAddStageToPlots(stageId, [plotNumber]);
    } else {
      handleRemoveStageFromPlot(stageId, plotNumber);
    }
  };

  const getSelectableStagesForPlot = (plotNumber: string) => {
    return availableStages.filter((stage) => {
      const stageInfo = getStageInfo({ plotNumber, stage });
      return !stageInfo.isFullyCompleted;
    });
  };

  const handleToggleAllStages = (plotNumber: string, checked: boolean) => {
    if (checked) {
      const selectable = getSelectableStagesForPlot(plotNumber);
      selectable.forEach((stage) => {
        handleAddStageToPlots(stage, [plotNumber]);
      });
    } else {
      bulkStageSelections.forEach((selection) => {
        if (selection.plotNumbers.includes(plotNumber)) {
          handleRemoveStageFromPlot(selection.stageId, plotNumber);
        }
      });
    }
  };

  const getStageInfo = ({
    plotNumber,
    stage
  }: {
    plotNumber: string;
    stage: string;
  }) => {
    // Find the plot and stage in projectCentralRepoData
    const plot = projectCentralRepoData.groundworks.plots.find(
      (p) => p.plotNo === plotNumber
    );
    const stageData = plot?.stages?.find((s) => s.stageName === stage);
    const tasks = stageData?.tasks ?? [];
    const taskNames = tasks.map((t) => t.taskName);

    // Check task completion status from central repository
    const completedFromPrevious = tasks
      .filter((t) => t.currentStatus === "Completed")
      .map((t) => ({
        taskName: t.taskName,
        month: t.lastUpdatedInMonth ?? ""
      }));

    const remainingTasks = taskNames.filter(
      (task) => !completedFromPrevious.some((ct) => ct.taskName === task)
    );
    const totalTaskCount = taskNames.length;
    const completedTaskCount = completedFromPrevious.length;
    const isFullyCompleted = totalTaskCount > 0 && remainingTasks.length === 0;
    const isPartial = completedTaskCount > 0 && !isFullyCompleted;

    return {
      taskNames,
      completedFromPrevious,
      remainingTasks,
      totalTaskCount,
      completedTaskCount,
      isFullyCompleted,
      isPartial
    };
  };

  const totalStages = availableStages.length * selectedPlots.length;

  const getGlobalStats = () => {
    if (selectedPlots.length === 0)
      return { completedPrevious: 0, partiallyDone: 0, availableToSelect: 0 };

    let completedPrevious = 0;
    let partiallyDone = 0;
    let availableToSelect = 0;

    // Aggregate stats across all selected plots
    selectedPlots.forEach((plotNumber) => {
      availableStages.forEach((stage) => {
        const stageInfo = getStageInfo({ plotNumber, stage });

        if (stageInfo.isFullyCompleted) {
          completedPrevious++;
        } else if (stageInfo.isPartial) {
          partiallyDone++;
          availableToSelect++;
        } else {
          availableToSelect++;
        }
      });
    });

    return { completedPrevious, partiallyDone, availableToSelect };
  };

  const globalStats = getGlobalStats();

  const isAllAvailableSelected =
    selectedPlots.length > 0 &&
    globalStats.availableToSelect > 0 &&
    selectedPlots.every((plotNumber) => {
      const selectable = getSelectableStagesForPlot(plotNumber);
      if (selectable.length === 0) return true;
      const plotStageIds = bulkStageSelections
        .filter((s) => s.plotNumbers.includes(plotNumber))
        .map((s) => s.stageId);
      return selectable.every((stage) => plotStageIds.includes(stage));
    });

  const handleToggleAllStagesGlobal = (checked: boolean) => {
    if (checked) {
      selectedPlots.forEach((plotNumber) => {
        getSelectableStagesForPlot(plotNumber).forEach((stage) => {
          handleAddStageToPlots(stage, [plotNumber]);
        });
      });
    } else {
      setBulkStageSelections([]);
    }
  };

  return (
    <div>
      {/* Summary Cards - Top Level */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              border: "1px solid #f0f0f0"
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#DBEAFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <BsBoxSeam className="text-[#0EA5E9] text-[14px]" />
            </div>
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: "#262626"
                }}
              >
                {totalStages}
              </div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>Total Stages</div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              border: "1px solid #f0f0f0"
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#DCFCE7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: "#262626"
                }}
              >
                {globalStats.completedPrevious}
              </div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                Completed (Previous)
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              border: "1px solid #f0f0f0"
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#FEF3C7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <FaRegClock className="text-[16px] text-[#F59E0B]" />
            </div>
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: "#262626"
                }}
              >
                {globalStats.partiallyDone}
              </div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                Partially Done
              </div>
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              border: "1px solid #f0f0f0"
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#F3E8FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <PlusOutlined style={{ color: "#A855F7", fontSize: 16 }} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: "#262626"
                }}
              >
                {globalStats.availableToSelect}
              </div>
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                Available to Select
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Info Alert - Top Level - Only show if there are completed stages from previous forecasts */}
      {(globalStats.completedPrevious > 0 || globalStats.partiallyDone > 0) && (
        <div
          style={{
            background: "#FFFBEA",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 16,
            display: "flex",
            alignItems: "flex-start",
            gap: 16
          }}
        >
          <span className="h-10 w-10 rounded-lg text-base bg-[#FFF3CC] text-[#FFA100] inline-flex justify-center items-center">
            ⓘ
          </span>
          <div>
            <p className="text-base text-[#1e1e1e]">
              Select available stages for forecasting
            </p>
            <p className="mt-0.5 text-sm text-[#787878]">
              Note that completed stages are non-selectable and partially
              completed stages display task-level progress.
            </p>
          </div>
        </div>
      )}

      {/* Legend - Top Level */}
      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 16,
          flexWrap: "wrap"
        }}
      >
        <Space size={4}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#52c41a",
              display: "inline-block"
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Completed (Not Selectable)
          </Text>
        </Space>
        <Space size={4}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#fa8c16",
              display: "inline-block"
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Partially Completed
          </Text>
        </Space>
        <Space size={4}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#1890ff",
              display: "inline-block"
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Selected
          </Text>
        </Space>
        <Space size={4}>
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#d9d9d9",
              display: "inline-block"
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Not Started
          </Text>
        </Space>
      </div>

      {/* Global Select All — feature-flagged, local + dev only */}
      {features.globalSelectAll && globalStats.availableToSelect > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 12
          }}
        >
          <Button
            type={isAllAvailableSelected ? "default" : "primary"}
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={() => handleToggleAllStagesGlobal(!isAllAvailableSelected)}
          >
            {isAllAvailableSelected
              ? "Clear All"
              : "Select All Available (All Plots)"}
          </Button>
        </div>
      )}

      {/* Plot Accordions */}
      <Space direction="vertical" style={{ width: "100%" }} size="small">
        {selectedPlots.map((plotNumber) => {
          const plotStages = bulkStageSelections
            .filter((selection) => selection.plotNumbers.includes(plotNumber))
            .map((selection) => selection.stageId);

          const selectableStages = getSelectableStagesForPlot(plotNumber);
          const isExpanded = expandedPlots.includes(plotNumber);

          return (
            <div key={plotNumber}>
              {/* Plot Accordion Header */}
              <div
                onClick={() => togglePlotExpanded(plotNumber)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  background: "#fff",
                  border: "1px solid #e8e8e8",
                  borderRadius: isExpanded ? "8px 8px 0 0" : "8px",
                  cursor: "pointer"
                }}
              >
                <Space size="middle">
                  <span
                    style={{
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s"
                    }}
                    className="inline-flex justify-center items-center h-8 w-8 bg-[#f1f5f9] rounded-lg"
                  >
                    <DownOutlined className="text-[10px] text-[#64748b]" />
                  </span>
                  <Text strong>Plot {plotNumber}</Text>
                  <Text type="secondary" style={{ color: "#1890ff" }}>
                    {plotStages.length} of {availableStages.length} stages
                  </Text>
                </Space>
                {isExpanded && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleAllStages(
                        plotNumber,
                        plotStages.length !== selectableStages.length
                      );
                    }}
                  >
                    Select All Available
                  </Button>
                )}
              </div>

              {/* Expanded Content - Stage Cards directly */}
              {isExpanded && (
                <div
                  style={{
                    background: "#fafafa",
                    border: "1px solid #e8e8e8",
                    borderTop: "none",
                    borderRadius: "0 0 8px 8px",
                    padding: "16px"
                  }}
                >
                  <Row gutter={[12, 12]}>
                    {availableStages.map((stage) => {
                      const stageInfo = getStageInfo({ plotNumber, stage });
                      const {
                        taskNames,
                        completedFromPrevious,
                        remainingTasks,
                        totalTaskCount,
                        completedTaskCount,
                        isFullyCompleted,
                        isPartial
                      } = stageInfo;
                      const isSelected = plotStages.includes(stage);

                      // Determine card styling based on state
                      let borderColor = "#e8e8e8";
                      let backgroundColor = "#fff";
                      let statusBadge: React.ReactNode = null;
                      let progressColor = "#d9d9d9";

                      if (isFullyCompleted) {
                        borderColor = "#b7eb8f";
                        backgroundColor = "#f6ffed";
                        statusBadge = (
                          <Tag
                            color="success"
                            style={{
                              fontSize: 10,
                              padding: "0 6px",
                              borderRadius: 4
                            }}
                          >
                            Done
                          </Tag>
                        );
                        progressColor = "#52c41a";
                      } else if (isPartial) {
                        borderColor = "#ffd591";
                        backgroundColor = "#fff7e6";
                        statusBadge = (
                          <Tag
                            color="warning"
                            style={{
                              fontSize: 10,
                              padding: "0 6px",
                              borderRadius: 4
                            }}
                          >
                            Partial
                          </Tag>
                        );
                        progressColor = "#fa8c16";
                      } else if (isSelected) {
                        borderColor = "#91d5ff";
                        backgroundColor = "#e6f7ff";
                        progressColor = "#1890ff";
                      }

                      const tooltipContent =
                        remainingTasks.length > 0 ? (
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                marginBottom: 8
                              }}
                            >
                              TASK STATUS
                            </div>
                            {taskNames.map((task, idx) => {
                              const isTaskCompleted =
                                completedFromPrevious.some(
                                  (ct) => ct.taskName === task
                                );
                              return (
                                <div
                                  key={idx}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    padding: "4px 0",
                                    borderBottom:
                                      idx < taskNames.length - 1
                                        ? "1px solid #f0f0f0"
                                        : "none"
                                  }}
                                >
                                  <Space size={4}>
                                    {isTaskCompleted ? (
                                      <CheckCircleOutlined
                                        style={{ color: "#52c41a" }}
                                      />
                                    ) : (
                                      <span
                                        style={{
                                          width: 12,
                                          height: 12,
                                          borderRadius: "50%",
                                          border: "1px solid #d9d9d9",
                                          display: "inline-block"
                                        }}
                                      />
                                    )}
                                    <span style={{ fontSize: 12 }}>{task}</span>
                                  </Space>
                                  <Text
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 11,
                                      color: isTaskCompleted
                                        ? "#52c41a"
                                        : "#8c8c8c"
                                    }}
                                  >
                                    {isTaskCompleted ? "Done" : "Pending"}
                                  </Text>
                                </div>
                              );
                            })}
                          </div>
                        ) : taskNames.length > 0 ? (
                          <div style={{ fontSize: 11 }}>
                            All tasks completed in previous forecasts
                          </div>
                        ) : (
                          "No tasks mapped to this stage"
                        );

                      return (
                        <Col key={stage} xs={24} sm={12} md={8} lg={6}>
                          <Tooltip
                            title={tooltipContent}
                            placement="right"
                            overlayStyle={{ maxWidth: 300 }}
                          >
                            <div
                              onClick={() => {
                                if (!isFullyCompleted) {
                                  handleToggleStage(
                                    plotNumber,
                                    stage,
                                    !isSelected
                                  );
                                }
                              }}
                              style={{
                                background: backgroundColor,
                                border: `1px solid ${borderColor}`,
                                borderRadius: 8,
                                padding: 12,
                                cursor: isFullyCompleted
                                  ? "not-allowed"
                                  : "pointer",
                                opacity: isFullyCompleted ? 0.7 : 1,
                                position: "relative",
                                minHeight: 100
                              }}
                            >
                              {/* Checkbox */}
                              <div
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  left: 8
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  disabled={isFullyCompleted}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleToggleStage(
                                      plotNumber,
                                      stage,
                                      e.target.checked
                                    );
                                  }}
                                />
                              </div>

                              {/* Status indicator dot */}
                              <div
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  background: isFullyCompleted
                                    ? "#52c41a"
                                    : isPartial
                                      ? "#fa8c16"
                                      : isSelected
                                        ? "#1890ff"
                                        : "#d9d9d9"
                                }}
                              />

                              {/* Stage Name & Status */}
                              <div style={{ marginTop: 20 }}>
                                <Space size={4} style={{ marginBottom: 4 }}>
                                  <Text
                                    strong
                                    style={{
                                      fontSize: 13,
                                      color: isFullyCompleted
                                        ? "#8c8c8c"
                                        : "#262626"
                                    }}
                                  >
                                    {stage}
                                  </Text>
                                  {statusBadge}
                                </Space>

                                {/* Task count */}
                                <div style={{ marginBottom: 8 }}>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 11 }}
                                  >
                                    {totalTaskCount} tasks
                                    {completedTaskCount > 0 &&
                                      ` • ${completedTaskCount} completed`}
                                  </Text>
                                </div>

                                {/* Progress bar */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8
                                  }}
                                >
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 11 }}
                                  >
                                    Tasks
                                  </Text>
                                  <div
                                    style={{
                                      flex: 1,
                                      height: 4,
                                      background: "#f0f0f0",
                                      borderRadius: 2,
                                      overflow: "hidden"
                                    }}
                                  >
                                    <div
                                      style={{
                                        width:
                                          totalTaskCount > 0
                                            ? `${
                                                (completedTaskCount /
                                                  totalTaskCount) *
                                                100
                                              }%`
                                            : "0%",
                                        height: "100%",
                                        background: progressColor,
                                        borderRadius: 2
                                      }}
                                    />
                                  </div>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 11 }}
                                  >
                                    {completedTaskCount}/{totalTaskCount}
                                  </Text>
                                </div>
                              </div>
                            </div>
                          </Tooltip>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
            </div>
          );
        })}
      </Space>
    </div>
  );
};
